import type { WorkspaceEdge, WorkspaceNode, WorkspaceNodeType } from "../../../types/workspace";

/** Inverse of backend ALLOWED_CHILDREN — who may parent each node type. */
export const WORKSPACE_ALLOWED_PARENT_TYPES: Record<WorkspaceNodeType, WorkspaceNodeType[]> = {
  account: [],
  strategy: ["account"],
  report: ["account", "strategy"],
  /** Includes policy for derived Edit & run (source policy → derived policy → sim). */
  simulation: ["account", "report", "simulation", "policy"],
  /** Includes policy for derived Edit & run forks. */
  policy: ["simulation", "policy"],
  /** Includes combined_policy for single-parent derived forks (combine still ≥2 policy parents). */
  combined_policy: ["policy", "combined_policy"],
  combined_simulation: ["combined_policy", "combined_simulation"],
};

/** Node types that may have multiple incoming edges. */
export const WORKSPACE_MULTI_PARENT_TYPES = new Set<WorkspaceNodeType>(["combined_policy"]);

export const MIN_COMBINED_POLICY_PARENTS = 2;

function childrenByParent(edges: WorkspaceEdge[]): Map<string, string[]> {
  const map = new Map<string, string[]>();
  for (const edge of edges) {
    const list = map.get(edge.sourceNodeId) ?? [];
    list.push(edge.targetNodeId);
    map.set(edge.sourceNodeId, list);
  }
  return map;
}

function parentIdFor(edges: WorkspaceEdge[], nodeId: string): string | null {
  return edges.find((edge) => edge.targetNodeId === nodeId)?.sourceNodeId ?? null;
}

export function parentIdsFor(edges: WorkspaceEdge[], nodeId: string): string[] {
  return edges.filter((edge) => edge.targetNodeId === nodeId).map((edge) => edge.sourceNodeId);
}

/** True if `maybeDescendantId` is `ancestorId` or below it in the graph. */
export function isWorkspaceNodeDescendant(
  ancestorId: string,
  maybeDescendantId: string,
  edges: WorkspaceEdge[]
): boolean {
  if (ancestorId === maybeDescendantId) return true;
  const children = childrenByParent(edges);
  const stack = [...(children.get(ancestorId) ?? [])];
  const seen = new Set<string>();
  while (stack.length > 0) {
    const current = stack.pop()!;
    if (seen.has(current)) continue;
    seen.add(current);
    if (current === maybeDescendantId) return true;
    stack.push(...(children.get(current) ?? []));
  }
  return false;
}

/**
 * Valid "Move under…" targets for `node`: allowed parent types, not self,
 * not current parent, and not a descendant (would create a cycle).
 *
 * Simulations are permanently bound to the report whose trade data they were
 * run against. Reparenting a sim under a different report would present results
 * against the wrong dataset. They may hang under Account (disconnected), but
 * "Move under…" may only restore them to that same matched report — never
 * another one (`simulationSourceReportIdByEntityId` / current report parent).
 */
export function listWorkspaceReparentCandidates(
  node: WorkspaceNode,
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[],
  options?: {
    simulationSourceReportIdByEntityId?: Record<string, string>;
  }
): WorkspaceNode[] {
  if (node.type === "account") return [];
  // Combined nodes are defined by their source lineage — do not rewire.
  if (node.type === "combined_policy" || node.type === "combined_simulation") return [];

  const allowed = new Set(WORKSPACE_ALLOWED_PARENT_TYPES[node.type] ?? []);
  if (allowed.size === 0) return [];

  const currentParentId = parentIdFor(edges, node.id);
  const currentParent = currentParentId
    ? nodes.find((entry) => entry.id === currentParentId) ?? null
    : null;

  const matchedReportEntityId =
    node.type === "simulation"
      ? options?.simulationSourceReportIdByEntityId?.[node.entityId]?.trim() ||
        (currentParent?.type === "report" ? currentParent.entityId.trim() : "") ||
        ""
      : "";

  return nodes
    .filter((candidate) => {
      if (candidate.id === node.id) return false;
      if (candidate.id === currentParentId) return false;
      if (!allowed.has(candidate.type)) return false;
      if (candidate.isDisabled) return false;
      if (node.type === "simulation" && candidate.type === "report") {
        // Only the matched report (if known). Unmatched/standalone sims cannot
        // be moved under an arbitrary report.
        if (!matchedReportEntityId || candidate.entityId.trim() !== matchedReportEntityId) {
          return false;
        }
      }
      // Never parent under a node in this node's subtree.
      if (isWorkspaceNodeDescendant(node.id, candidate.id, edges)) return false;
      return true;
    })
    .sort((a, b) => {
      const typeOrder = (WORKSPACE_ALLOWED_PARENT_TYPES[node.type] ?? []).indexOf(a.type)
        - (WORKSPACE_ALLOWED_PARENT_TYPES[node.type] ?? []).indexOf(b.type);
      if (typeOrder !== 0) return typeOrder;
      return a.id.localeCompare(b.id);
    });
}

/**
 * Target for "Disconnect": keep the node on the canvas, detach from its mid-chain
 * parent, and hang it under Account so it can be moved under something else later.
 *
 * Returns null when already under Account, has no parent, or Account cannot legally
 * parent this node type (e.g. policy).
 */
export function findWorkspaceDisconnectTarget(
  node: WorkspaceNode,
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[]
): WorkspaceNode | null {
  if (node.type === "account") return null;

  const allowed = WORKSPACE_ALLOWED_PARENT_TYPES[node.type] ?? [];
  if (!allowed.includes("account")) return null;

  const currentParentId = parentIdFor(edges, node.id);
  if (!currentParentId) return null;

  const currentParent = nodes.find((entry) => entry.id === currentParentId);
  if (!currentParent || currentParent.type === "account") return null;

  return nodes.find((entry) => entry.type === "account") ?? null;
}
